/**
 * Learner / site-discovery client tools — raw facts for the LLM to interpret.
 *
 * inspectPage, queryDOM, startNetworkCapture, stopNetworkCapture
 */

// ============================================================
// inspectPage
// ============================================================

export function inspectPage() {
  return {
    url: window.location.href,
    title: document.title,
    platform: detectPlatform(),
    framework: detectFramework(),
    globals: detectGlobals(),
    scripts: getScriptsSummary(),
    body_classes: document.body?.className || '',
    meta: getMetaTags(),
    schema_org: getSchemaOrg(),
  };
}

function detectPlatform() {
  const signals = [];
  let detected = 'custom';

  if (window.Shopify) {
    detected = 'shopify';
    signals.push('window.Shopify');
  }
  if (
    document.body?.classList?.contains('woocommerce') ||
    document.querySelector('link[href*="woocommerce"]')
  ) {
    detected = 'woocommerce';
    signals.push('body.woocommerce');
  }
  const amdRequire = typeof window !== 'undefined' ? window['require'] : undefined;
  if (amdRequire && amdRequire.s?.contexts) {
    detected = 'magento';
    signals.push('require.s.contexts');
  }
  if (window.wixBiSession || window.viewerModel) {
    detected = 'wix';
    signals.push('wixBiSession');
  }
  if (window.BCData) {
    detected = 'bigcommerce';
    signals.push('window.BCData');
  }

  return { detected, signals };
}

function detectFramework() {
  const signals = [];
  let detected = 'vanilla';

  if (window.__NEXT_DATA__) {
    detected = 'next';
    signals.push('window.__NEXT_DATA__');
  } else {
    const root =
      document.getElementById('root') ||
      document.getElementById('app') ||
      document.querySelector('body > div');
    if (root) {
      const fiberKey = Object.keys(root).find(
        (k) => k.startsWith('__reactFiber') || k.startsWith('__reactInternalInstance')
      );
      if (fiberKey) {
        detected = 'react';
        signals.push('__reactFiber on DOM');
      }
    }
  }

  if (detected === 'vanilla') {
    if (window.__NUXT__) {
      detected = 'nuxt';
      signals.push('window.__NUXT__');
    } else if (window.__VUE__ || document.querySelector('[data-v-]')) {
      detected = 'vue';
      signals.push(window.__VUE__ ? 'window.__VUE__' : 'data-v- attrs');
    }
  }

  if (detected === 'vanilla') {
    const ng = document.querySelector('[ng-version]');
    if (ng) {
      detected = 'angular';
      signals.push(`ng-version: ${ng.getAttribute('ng-version')}`);
    }
  }

  return { detected, signals };
}

function detectGlobals() {
  const candidates = [
    '__NEXT_DATA__',
    '__NUXT__',
    '__VUE__',
    '__INITIAL_STATE__',
    '__PRELOADED_STATE__',
    '__REDUX_DEVTOOLS_EXTENSION__',
    'Shopify',
    'BCData',
    'wixBiSession',
    'viewerModel',
    'algoliasearch',
    'algoliaConfig',
    '__algoliaSearch',
    'dataLayer',
    'gtag',
    'ga',
    'jQuery',
    '$',
  ];
  return candidates.filter((key) => {
    try {
      return window[key] !== undefined;
    } catch {
      return false;
    }
  });
}

function getScriptsSummary() {
  return Array.from(document.querySelectorAll('script[src]'))
    .map((s) => {
      try {
        const url = new URL(s.src);
        return url.pathname.split('/').pop() || url.hostname;
      } catch {
        return s.src.substring(0, 80);
      }
    })
    .filter(Boolean)
    .slice(0, 20);
}

function getMetaTags() {
  const result = {};
  ['generator', 'theme-color', 'application-name'].forEach((name) => {
    const el = document.querySelector(`meta[name="${name}"]`);
    if (el) result[name] = el.content;
  });
  const ogSiteName = document.querySelector('meta[property="og:site_name"]');
  if (ogSiteName) result['og:site_name'] = ogSiteName.content;
  return result;
}

function getSchemaOrg() {
  const schemas = [];
  document.querySelectorAll('script[type="application/ld+json"]').forEach((el) => {
    try {
      const data = JSON.parse(el.textContent);
      const items = Array.isArray(data) ? data : [data];
      items.forEach((item) => {
        if (item['@type']) {
          schemas.push({
            type: item['@type'],
            name: item.name?.substring(0, 100) || null,
            ...(item['@type'] === 'Product' && item.offers
              ? {
                  price: item.offers.price || item.offers.lowPrice,
                  currency: item.offers.priceCurrency,
                }
              : {}),
          });
        }
      });
    } catch {
      /* malformed JSON-LD */
    }
  });
  return schemas.slice(0, 10);
}

// ============================================================
// queryDOM
// ============================================================

export function queryDOM(selector, options = {}) {
  const { mode = 'outer', limit = 5, depth = 3 } = options;

  if (mode === 'count') {
    return { count: document.querySelectorAll(selector).length };
  }

  const elements = Array.from(document.querySelectorAll(selector)).slice(0, limit);

  return elements.map((el) => {
    switch (mode) {
      case 'text':
        return el.textContent?.trim()?.substring(0, 1000);
      case 'attrs':
        return getElementAttrs(el);
      case 'inner':
        return truncateWithDepth(el, depth, 'inner');
      case 'outer':
      default:
        return truncateWithDepth(el, depth, 'outer');
    }
  });
}

function truncateWithDepth(el, maxDepth, type) {
  const clone = el.cloneNode(true);
  pruneDepth(clone, maxDepth, 0);
  return type === 'inner' ? clone.innerHTML : clone.outerHTML;
}

function pruneDepth(node, maxDepth, currentDepth) {
  if (currentDepth >= maxDepth) {
    Array.from(node.children).forEach((child) => {
      if (child.children.length > 0) {
        child.innerHTML = `[${child.children.length} children]`;
      }
    });
    return;
  }
  Array.from(node.children).forEach((child) => {
    pruneDepth(child, maxDepth, currentDepth + 1);
  });
}

function getElementAttrs(el) {
  const attrs = {};
  Array.from(el.attributes).forEach((a) => {
    attrs[a.name] = a.value?.substring(0, 200);
  });
  return {
    tag: el.tagName.toLowerCase(),
    attrs,
    childCount: el.children.length,
    textPreview: el.textContent?.trim()?.substring(0, 100),
  };
}

// ============================================================
// Network capture
// ============================================================

let _capturedRequests = [];
let _originalFetch = null;
let _originalXHROpen = null;
let _originalXHRSend = null;

export function startNetworkCapture() {
  _capturedRequests = [];

  _originalFetch = window.fetch;
  window.fetch = async function fetchCapture(...args) {
    const request = new Request(...args);
    const entry = {
      type: 'fetch',
      url: request.url,
      method: request.method,
      requestHeaders: Object.fromEntries(request.headers.entries()),
      requestBody: null,
      responseStatus: null,
      responseHeaders: null,
      responseBody: null,
      timestamp: Date.now(),
    };

    try {
      if (request.body) {
        const cloned = request.clone();
        entry.requestBody = await cloned.text().catch(() => '[unreadable]');
      }
    } catch {
      /* ignore */
    }

    try {
      const response = await _originalFetch.apply(this, args);
      entry.responseStatus = response.status;
      entry.responseHeaders = Object.fromEntries(response.headers.entries());

      const cloned = response.clone();
      try {
        const text = await cloned.text();
        entry.responseBody = text.substring(0, 5000);
      } catch {
        entry.responseBody = '[unreadable]';
      }

      _capturedRequests.push(entry);
      return response;
    } catch (err) {
      entry.error = err.message;
      _capturedRequests.push(entry);
      throw err;
    }
  };

  _originalXHROpen = XMLHttpRequest.prototype.open;
  _originalXHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function openCapture(method, url, ...rest) {
    this._captureData = {
      method,
      url: typeof url === 'string' ? url : String(url),
      type: 'xhr',
      requestHeaders: {},
      timestamp: Date.now(),
    };
    return _originalXHROpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function sendCapture(body) {
    if (this._captureData) {
      this._captureData.requestBody =
        typeof body === 'string' ? body?.substring(0, 5000) : null;
      this.addEventListener('load', () => {
        if (!this._captureData) return;
        this._captureData.responseStatus = this.status;
        this._captureData.responseBody = this.responseText?.substring(0, 5000) || null;
        try {
          this._captureData.responseHeaders = this.getAllResponseHeaders()
            .split('\r\n')
            .filter(Boolean)
            .reduce((acc, line) => {
              const [key, ...val] = line.split(': ');
              acc[key] = val.join(': ');
              return acc;
            }, {});
        } catch {
          /* ignore */
        }
        _capturedRequests.push(this._captureData);
      });
    }
    return _originalXHRSend.call(this, body);
  };

  return { status: 'capturing' };
}

export function stopNetworkCapture() {
  if (_originalFetch) {
    window.fetch = _originalFetch;
    _originalFetch = null;
  }
  if (_originalXHROpen) {
    XMLHttpRequest.prototype.open = _originalXHROpen;
    _originalXHROpen = null;
  }
  if (_originalXHRSend) {
    XMLHttpRequest.prototype.send = _originalXHRSend;
    _originalXHRSend = null;
  }

  const results = [..._capturedRequests];
  _capturedRequests = [];

  const noise =
    /\.(png|jpg|jpeg|gif|svg|webp|ico|css|woff2?|ttf|eot)(\?|$)|google-analytics|googletagmanager|facebook\.com\/tr|doubleclick\.net|hotjar\.com|clarity\.ms/i;
  const filtered = results.filter((r) => !noise.test(r.url));

  return { total: results.length, filtered: filtered.length, requests: filtered };
}
